import { MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

type AdminMobileRowActionItem = {
  label: string;
  onSelect?: () => void;
  to?: string;
  tone?: 'default' | 'danger';
};

type AdminMobileRowActionsProps = {
  title: string;
  description?: string;
  items: AdminMobileRowActionItem[];
  triggerLabel?: string;
  triggerTestId?: string;
};

const getActionClassName = (tone: AdminMobileRowActionItem['tone']) =>
  cn(
    'inline-flex w-full items-center justify-center rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
    tone === 'danger'
      ? 'border-red-200 text-red-700 hover:bg-red-50'
      : 'border-slate-300 text-slate-700 hover:bg-slate-50',
  );

const AdminMobileRowActions = ({
  title,
  description = 'Choose an action for this row.',
  items,
  triggerLabel,
  triggerTestId,
}: AdminMobileRowActionsProps) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50 sm:hidden"
        aria-label={triggerLabel || `Open actions for ${title}`}
        data-testid={triggerTestId}
        onClick={(event) => event.stopPropagation()}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </DialogTrigger>
    <DialogContent className="left-4 right-4 top-auto bottom-4 w-auto max-w-none translate-x-0 translate-y-0 rounded-2xl border border-slate-200 bg-white p-0 sm:left-[50%] sm:right-auto sm:top-[50%] sm:bottom-auto sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2">
      <DialogHeader className="border-b border-slate-200 px-4 py-4 text-left">
        <DialogTitle className="pr-8 text-base text-slate-900">{title}</DialogTitle>
        <DialogDescription className="pr-8 text-sm text-slate-500">{description}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2 p-4">
        {items.map((item) => {
          const key = `${item.label}-${item.to || 'action'}`;
          if (item.to) {
            return (
              <DialogClose asChild key={key}>
                <Link
                  to={item.to}
                  className={getActionClassName(item.tone)}
                  onClick={(event) => event.stopPropagation()}
                >
                  {item.label}
                </Link>
              </DialogClose>
            );
          }

          return (
            <DialogClose asChild key={key}>
              <button
                type="button"
                className={getActionClassName(item.tone)}
                onClick={(event) => {
                  event.stopPropagation();
                  item.onSelect?.();
                }}
              >
                {item.label}
              </button>
            </DialogClose>
          );
        })}
      </div>
    </DialogContent>
  </Dialog>
);

export default AdminMobileRowActions;
